import * as React from "react";

import type {
  HeroFilterState,
  HeroSortState,
  HeroToolbarColumn,
  HeroToolbarOption,
} from "@/components/hero-toolbar";

/**
 * hero-filter-bridge
 *
 * Canonical reference: ianfiles/ui-design-system.md §3.2 + §3.5
 *
 * Two responsibilities:
 *
 *   1. Resolve `HeroToolbarColumn` arrays from a small `FilterConfig` list of
 *      keyed filter dimensions a page wants — so a page declares the spec,
 *      not the menu plumbing.
 *
 *        const fooFilterConfig: FilterConfig = ["status", "salesRepId"];
 *        const heroColumns = useHeroFilterColumns(fooFilterConfig, {
 *          statusOptions: orderStatusOptions,
 *          optionOverrides: { salesRepId: salesReps.map(r => ({ value: r.id, label: r.name })) },
 *        });
 *
 *   2. Project the toolbar's internal chip state to either:
 *        a) flat query params for the worker (`useChipFilterParams`).
 *        b) client-side row sort/filter helpers (`applyHeroFilters`,
 *           `applyHeroSort`, `useHeroSortedRows`).
 *
 * The toolbar emits an opaque `HeroFilterState` (Record<columnKey, string[]>)
 * so the bridge knows how to translate keys like `salesRepId` into the
 * `salesRepId=…` query param the worker expects.
 */

/** Keys the bridge knows how to resolve options for out-of-the-box. */
export type FilterConfigKey =
  | "status"
  | "accountId"
  | "vendorId"
  | "salesRepId"
  | "ownerId"
  | "industry"
  | "vendorType"
  | "productClass"
  | "showArchived"
  | "showSuperseded";

/** A list of filter dimensions a page wants to enable. */
export type FilterConfig = readonly FilterConfigKey[];

/**
 * Options the consumer can pass into `useHeroFilterColumns` to provide
 * page-specific data (e.g. accounts list, sales reps). The keys mirror the
 * `FilterConfigKey` enum so a page can pass just what it needs.
 */
export interface HeroFilterColumnOptions {
  /** Status enum options for the active entity type. */
  statusOptions?: HeroToolbarOption[];
  /** Account select list (used by `accountId`). */
  accountOptions?: HeroToolbarOption[];
  /** Vendor select list (used by `vendorId`). */
  vendorOptions?: HeroToolbarOption[];
  /** Sales rep list (used by `salesRepId`). */
  salesRepOptions?: HeroToolbarOption[];
  /** Owner / assignee list (used by `ownerId`). */
  ownerOptions?: HeroToolbarOption[];
  /** Industry classification options (used by `industry`). */
  industryOptions?: HeroToolbarOption[];
  /** Vendor type options (used by `vendorType`). */
  vendorTypeOptions?: HeroToolbarOption[];
  /** Product class options (used by `productClass`). */
  productClassOptions?: HeroToolbarOption[];
  /** Catch-all override slot: drop a custom option list under any key. */
  optionOverrides?: Partial<Record<FilterConfigKey, HeroToolbarOption[]>>;
}

/** Default option labels shared by `showArchived` / `showSuperseded`. */
const TOGGLE_OPTIONS = {
  showArchived: [
    { value: "show", label: "Show archived" },
    { value: "only", label: "Only archived" },
  ],
  showSuperseded: [
    { value: "show", label: "Show superseded" },
    { value: "only", label: "Only superseded" },
  ],
} as const;

/**
 * Resolves a `FilterConfig` into the `HeroToolbarColumn[]` array the toolbar
 * needs. Memoized on the inputs so list pages can keep this stable.
 */
export function useHeroFilterColumns(
  config: FilterConfig,
  options: HeroFilterColumnOptions = {}
): HeroToolbarColumn[] {
  return React.useMemo(() => {
    return config.map((key): HeroToolbarColumn => {
      const labelMap: Record<FilterConfigKey, string> = {
        status: "Status",
        accountId: "Account",
        vendorId: "Vendor",
        salesRepId: "Sales Rep",
        ownerId: "Owner",
        industry: "Industry",
        vendorType: "Vendor Type",
        productClass: "Product Class",
        showArchived: "Archived",
        showSuperseded: "Superseded",
      };

      const optionSource: HeroToolbarOption[] | undefined =
        options.optionOverrides?.[key] ??
        (key === "status"
          ? options.statusOptions
          : key === "accountId"
            ? options.accountOptions
            : key === "vendorId"
              ? options.vendorOptions
              : key === "salesRepId"
                ? options.salesRepOptions
                : key === "ownerId"
                  ? options.ownerOptions
                  : key === "industry"
                    ? options.industryOptions
                    : key === "vendorType"
                      ? options.vendorTypeOptions
                      : key === "productClass"
                        ? options.productClassOptions
                        : key === "showArchived"
                          ? [...TOGGLE_OPTIONS.showArchived]
                          : key === "showSuperseded"
                            ? [...TOGGLE_OPTIONS.showSuperseded]
                            : undefined);

      const multi =
        key === "status" || key === "vendorType" || key === "industry";

      return {
        key,
        label: labelMap[key],
        filterable: true,
        sortable: false,
        groupable: false,
        multi,
        options: optionSource ?? [],
        // status / archived toggles are usually server-driven (the worker
        // filters before we paginate). Everything else defaults to client.
        clientFilter:
          key === "showArchived" || key === "showSuperseded" ? false : true,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, JSON.stringify(options)]);
}

/**
 * Project the toolbar's chip state to the flat `{ status, accountId, … }`
 * shape pages used to read from `useFilterQueryParams()`. Returns a stable
 * object keyed by `FilterConfigKey` so query hooks can spread it directly
 * into their fetch call.
 *
 * Multi-select columns (status, vendorType) emit comma-joined strings so the
 * worker can `split(",")` server-side. Single-select columns emit a single
 * string.
 */
export function chipFiltersToParams(
  config: FilterConfig,
  filters: HeroFilterState
): Partial<Record<FilterConfigKey, string>> {
  const out: Partial<Record<FilterConfigKey, string>> = {};
  for (const key of config) {
    const values = filters[key];
    if (!values || values.length === 0) continue;
    out[key] = values.join(",");
  }
  return out;
}

/**
 * Convenience hook: given a `FilterConfig` and the toolbar's `filters` chip
 * state, returns memoized query params. Pages that need both the chip array
 * AND the flat param object should call this directly.
 */
export function useChipFilterParams(
  config: FilterConfig,
  filters: HeroFilterState
): Partial<Record<FilterConfigKey, string>> {
  return React.useMemo(
    () => chipFiltersToParams(config, filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, JSON.stringify(filters)]
  );
}

// ─── Client-side row helpers ────────────────────────────────────────────────

/**
 * Returns rows filtered down by the currently-active client-side chip values.
 *
 * Only columns whose `clientFilter` is undefined or `true` participate.
 * `clientFilter: false` columns are skipped (the worker already filtered).
 *
 * The row's field name is assumed to be the column's `key` unless the caller
 * passes a `getRowValue(row, columnKey)` override.
 */
export function applyHeroFilters<T extends object>(
  rows: T[],
  columns: HeroToolbarColumn[],
  filters: HeroFilterState,
  getRowValue: (row: T, columnKey: string) => unknown = (row, key) =>
    (row as Record<string, unknown>)[key]
): T[] {
  if (Object.keys(filters).length === 0) return rows;

  const activeColumns = columns.filter(
    (c) => c.clientFilter !== false && (filters[c.key]?.length ?? 0) > 0
  );
  if (activeColumns.length === 0) return rows;

  return rows.filter((row) => {
    for (const col of activeColumns) {
      const allowed = filters[col.key]!;
      const value = getRowValue(row, col.key);
      const valueString = value == null ? "" : String(value);
      if (!allowed.includes(valueString)) return false;
    }
    return true;
  });
}

/** Apply a single sort selection to a row array. Returns a new array. */
export function applyHeroSort<T extends object>(
  rows: T[],
  sort: HeroSortState | null,
  getRowValue: (row: T, columnKey: string) => unknown = (row, key) =>
    (row as Record<string, unknown>)[key]
): T[] {
  if (!sort) return rows;
  const direction = sort.direction === "asc" ? 1 : -1;

  // Copy so we don't mutate the source
  return [...rows].sort((a, b) => {
    const av = getRowValue(a, sort.columnKey);
    const bv = getRowValue(b, sort.columnKey);

    // null / undefined sink to the end regardless of direction
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    // Numeric compare when both are numbers (or numeric strings)
    const an = typeof av === "number" ? av : Number(av);
    const bn = typeof bv === "number" ? bv : Number(bv);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) {
      return (an - bn) * direction;
    }

    // Date compare when both parse as dates
    const ad = typeof av === "string" ? Date.parse(av) : NaN;
    const bd = typeof bv === "string" ? Date.parse(bv) : NaN;
    if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
      return (ad - bd) * direction;
    }

    // String compare with locale-aware ordering
    return String(av).localeCompare(String(bv)) * direction;
  });
}

/**
 * Combined sorted view: returns rows with the active hero sort applied
 * (client-side). Pages should pass this as the `data` prop on `DataTable`.
 *
 * NOTE: `applyHeroFilters` is intentionally NOT chained here. Most list
 * pages run filters server-side via `useChipFilterParams`, so client
 * filtering would double-apply. Pages that need client-side filtering can
 * call `applyHeroFilters` manually before `useHeroSortedRows`.
 */
export function useHeroSortedRows<T extends Record<string, unknown>>(
  rows: T[],
  _columns: HeroToolbarColumn[],
  sort: HeroSortState | null,
  getRowValue?: (row: T, columnKey: string) => unknown
): T[] {
  return React.useMemo(
    () => applyHeroSort(rows, sort, getRowValue),
    [rows, sort, getRowValue]
  );
}
